import { useEffect, useState } from "react";
import { fetchCourses } from "../../utils/wellspoken.api";
import { useNavigate } from "react-router-dom";
import { deleteCourse } from "../../utils/wellspoken.api";
import { createOrEditCourse } from "../../utils/wellspoken.api";
import toast from "react-hot-toast";


export default function CoursesList() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const [editingCourse, setEditingCourse] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;

    fetchCourses()
      .then((data) => {
        if (isMounted) {
          setCourses(data || []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setError("Unable to load courses at the moment.");
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);


  const handleEditSubmit = async () => {
    try {
      setIsSaving(true);

      // 🔹 CREATE FORMDATA (matches curl exactly)
      const formData = new FormData();
      formData.append('CourseId', editingCourse.courseId);
      formData.append('CourseName', editingCourse.courseName);
      formData.append('CourseDescription', editingCourse.courseDescription);
      formData.append('CourseDuration', editingCourse.courseDuration);
      formData.append('Price', editingCourse.price);
      formData.append('CourseLevel', editingCourse.courseLevel);
      formData.append('CourseMode', editingCourse.courseMode);
      formData.append('StartDate', editingCourse.startDate);
      formData.append('EndDate', editingCourse.endDate);

      // Handle banner file properly
      if (editingCourse.banner && editingCourse.banner instanceof File) {
        formData.append('Banner', editingCourse.banner);
      }

      // 🔹 CALL API
      await createOrEditCourse(formData);
      toast.success("Course Edited successfully 🎉");
      // 🔹 OPTIMISTIC UI UPDATE
      setCourses((prev) =>
        prev.map((course) =>
          course.courseId === editingCourse.courseId ? editingCourse : course
        )
      );

      setEditingCourse(null);
    } catch (error) {
      console.error('Edit failed:', error);
      alert("Failed to update course");
    } finally {
      setIsSaving(false);
    }
  };



  /* ---------------- Delete (Frontend Only) ---------------- */
  const handleDelete = async (courseId) => {
    console.log("courseid", courseId)
    const confirmed = window.confirm(
      "Are you sure you want to delete this course?"
    );

    if (!confirmed) return;

    try {
      // Optimistic UI update (instant UX)
      setCourses((prev) =>
        prev.filter((course) => course.courseId !== courseId)
      );

      await deleteCourse(courseId);
      toast.success("Course Deleted successfully 🎉");
    } catch (err) {
      alert("Failed to delete course. Please try again.");

      // Optional rollback (safe fallback)
      fetchCourses()
        .then((data) => setCourses(data || []))
        .catch(() => { });
    }
  };


  return (
    <main className="bg-surface min-h-screen pt-15 pb-16">
      {/* Page Header */}
      <section
        aria-labelledby="courses-heading"
        className="max-w-7xl mx-auto px-4 text-center"
      >
        <h1
          id="courses-heading"
          className="text-3xl sm:text-4xl font-bold text-textPrimary"
        >
          Our Courses
        </h1>
        <p className="mt-4 text-textSecondary max-w-2xl mx-auto">
          Explore our industry-ready courses designed for beginners to advanced learners.
        </p>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-4 mt-12">
        {loading && (
          <p className="text-center text-textSecondary">Loading courses…</p>
        )}

        {error && (
          <p className="text-center text-primary-light">{error}</p>
        )}

        {!loading && !error && (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {courses.map((course) => (
              <article
                key={course.courseId}
                className="
                  bg-white border border-border rounded-2xl
                  shadow-sm hover:shadow-md transition
                  flex flex-col overflow-hidden
                  focus-within:ring-2 focus-within: ring-primary
                "
              >
                {/* Banner */}
                {/* <div className="aspect-[16/9] bg-surface overflow-hidden">
                  <img
                    src={
                      course.banner
                        ? course.banner.replace("\\", "/")
                        : "/images/course-placeholder.png"
                    }
                    alt={`${course.courseName} course banner`}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </div> */}

                {/* Body */}
                <div className="p-6 flex flex-col flex-1">
                  <h2 className="text-lg font-semibold text-textPrimary">
                    {course.courseName}
                  </h2>

                  {/* <p className="mt-2 text-sm text-textSecondary line-clamp-2">
                    {course.courseDescription}
                  </p> */}
                  <ul className="mt-2 space-y-2 text-sm text-textSecondary">
                    {Array.isArray(course.courseDescription)
                      ? course.courseDescription.map((f, i) => <li key={i}>• {f}</li>)
                      : course.courseDescription
                        ?.split("\n")
                        .map((f, i) => <li key={i}>• {f.trim()}</li>)}
                  </ul>
                  {/* Meta */}
                  <ul className="mt-4 space-y-1 text-sm text-textSecondary">
                    <li>
                      <strong className="text-textPrimary">Level:</strong>{" "}
                      {course.courseLevel}
                    </li>
                    <li>
                      <strong className="text-textPrimary">Duration:</strong>{" "}
                      {course.courseDuration} weeks
                    </li>
                    <li>
                      <strong className="text-textPrimary">Mode:</strong>{" "}
                      {course.courseMode}
                    </li>
                  </ul>

                  {/* Features */}
                  {Array.isArray(course.items) && (
                    <ul className="mt-4 space-y-2 text-sm">
                      {course.items.map((item, idx) => (
                        <li key={idx} className="flex gap-2 text-textSecondary">
                          <span aria-hidden="true">✔</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Footer */}
                  <div className="mt-auto pt-6 flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-lg font-bold text-primary">
                      ₹{Number(course.price ?? 0).toLocaleString("en-IN")}
                    </p>

                    <div className="flex gap-2">
                      {/* Edit */}
                      {/* <button
                        onClick={() =>
                          navigate(`/dashboard/edit-course`)
                        }
                        aria-label={`Edit ${course.CourseName}`}
                        className="
                          px-4 py-2 rounded-full text-sm font-medium
                          border border-border text-textPrimary
                          hover:bg-surface
                          focus-visible:ring-2 focus-visible:ring-primary-light
                        "
                      >
                        Edit
                      </button> */}
                      <button
                        onClick={() => setEditingCourse(course)}
                        className="px-6 py-2 rounded-full text-sm font-medium
                   bg-gradient-to-r from-blue-600 to-indigo-600
                    text-white
                   hover:from-blue-500 hover:to-indigo-500
                   focus-visible:outline-none
                   focus-visible:ring-2 focus-visible:ring-blue-500/40
                   disabled:opacity-60 transition
  "
                      >
                        Edit
                      </button>


                      {/* Delete */}
                      <button
                        onClick={() => handleDelete(course.courseId)}
                        aria-label={`Delete ${course.courseName}`}
                        className="
  px-4 py-2 rounded-full text-sm font-medium
  text-white bg-primary-light
  hover:opacity-90
  focus-visible:ring-2 focus-visible:ring-primary
"
                     >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {
        editingCourse && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <form
              onSubmit={(e) => {
                e.preventDefault();     //  PREVENT PAGE RELOAD
                handleEditSubmit();     //  CALL YOUR EXISTING LOGIC
              }}

              className="bg-white   focus-visible:ring-primary outline-none rounded-2xl p-6 w-full max-w-lg space-y-4">

              <h2 className="text-xl font-semibold">Edit Course</h2>

              <input
                value={editingCourse.courseName}
                onChange={(e) =>
                  setEditingCourse({
                    ...editingCourse,
                    courseName: e.target.value,
                  })
                }
                className="w-full border rounded px-3 py-2 "
                placeholder="Course Name"
                required
              />

              <textarea
                value={editingCourse.courseDescription}
                rows={3}
                onChange={(e) =>
                  setEditingCourse({
                    ...editingCourse,
                    courseDescription: e.target.value,
                  })
                }
                className="w-full border rounded px-3 py-2"
                 placeholder="Description"
              >
                {/* <option>Beginner</option>
              <option>Intermediate</option>
              <option>Advanced</option> */}
              </textarea>

              <input
                value={editingCourse.courseLevel}
                onChange={(e) =>
                  setEditingCourse({
                    ...editingCourse,
                    courseLevel: e.target.value,
                  })
                }
                className="w-full border rounded px-3 py-2 "
                placeholder="Level"
              />

              <input
                value={editingCourse.courseDuration}
                onChange={(e) =>
                  setEditingCourse({
                    ...editingCourse,
                    courseDuration: e.target.value,
                  })
                }
                className="w-full border rounded px-3 py-2"
                placeholder="Duration(weeks)"
              />

              <input
                value={editingCourse.price}
                onChange={(e) =>
                  setEditingCourse({
                    ...editingCourse,
                    price: e.target.value,
                  })
                }
                className="w-full border rounded px-3 py-2"
                placeholder="Price"
              />

              <div>
                <label className="block mb-1 text-sm font-medium">
                  Course Mode
                </label>

                <select
                  value={editingCourse.courseMode || ""}
                  onChange={(e) =>
                    setEditingCourse({
                      ...editingCourse,
                      courseMode: e.target.value,
                    })
                  }
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value="">Select Mode</option>
                  <option value="Online">Online</option>
                  <option value="Offline">Offline</option>
                </select>
              </div>

              {/* <input
              type="file"
              onChange={(e) =>
                setEditingCourse({
                  ...editingCourse,
                  banner: e.target.files[0],
                })
              }
            /> */}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingCourse(null)}
                  className="px-4 py-2 border rounded"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600
                    text-white
                   hover:from-blue-500 hover:to-indigo-500
                   focus-visible:outline-none
                   focus-visible:ring-2 focus-visible:ring-blue-500/40
                   disabled:opacity-60 transition rounded"
                >
                  {isSaving ? "Saving..." : "Update"}
                </button>
              </div>
            </form>
          </div>
        )
      }

    </main >
  );
}
